<template>
    <section class="files-manager pb-5">

        <div class="row mb-4">
            <div class="col-12">
                <b-card no-body class="shadow-sm border-0">
                    <div class="card-body">
                        <div class="upload-drop-zone p-5 text-center rounded transition-all" :class="{'is-dragover': $refs.upload && $refs.upload.dropActive}">

                            <div v-if="!files.length" class="empty-state">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="text-primary mb-3" viewBox="0 0 16 16">
                                    <path fill-rule="evenodd" d="M7.646 5.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 6.707V10.5a.5.5 0 0 1-1 0V6.707L6.354 7.854a.5.5 0 1 1-.708-.708l2-2z"/>
                                    <path d="M4.406 3.342A5.53 5.53 0 0 1 8 2c2.69 0 4.923 2 5.166 4.579C14.758 6.804 16 8.137 16 9.773 16 11.569 14.502 13 12.687 13H3.781C1.708 13 0 11.366 0 9.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383zm.653.757c-.757.653-1.153 1.44-1.153 2.056v.448l-.445.049C2.064 6.805 1 7.952 1 9.318 1 10.785 2.23 12 3.781 12h8.906C13.98 12 15 10.988 15 9.773c0-1.216-1.02-2.228-2.313-2.228h-.5v-.5C12.188 4.825 10.328 3 8 3a4.53 4.53 0 0 0-2.941 1.1z"/>
                                </svg>
                                <h4>{{ $t('forms.dropHere') }}</h4>
                                <p class="text-muted mb-4">{{ $t('forms.or') }}</p>

                                <file-upload
                                    class="btn btn-primary px-4 py-2 font-weight-bold"
                                    :post-action="uploadUrl" :multiple="true" :headers="authHeader"
                                    @input-file="inputFile" @input-filter="inputFilter"
                                    :drop="true" v-model="files" ref="fileUpload">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" class="mr-1" viewBox="0 0 16 16">
                                        <path d="M1 3.5A1.5 1.5 0 0 1 2.5 2h2.764c.958 0 1.76.56 2.311 1.184C7.985 3.648 8.48 4 9 4h4.5A1.5 1.5 0 0 1 15 5.5v.64c.57.265.94.876.856 1.546l-.64 5.124A2.5 2.5 0 0 1 12.733 15H3.266a2.5 2.5 0 0 1-2.481-2.19l-.64-5.124A1.5 1.5 0 0 1 1 6.14V3.5zM2 6h12v-.5a.5.5 0 0 0-.5-.5H9c-.964 0-1.71-.629-2.174-1.154C6.374 3.334 5.82 3 5.264 3H2.5a.5.5 0 0 0-.5.5V6zm-.367 1a.5.5 0 0 0-.496.562l.64 5.124A1.5 1.5 0 0 0 3.266 14h9.468a1.5 1.5 0 0 0 1.489-1.314l.64-5.124A.5.5 0 0 0 14.367 7H1.633z"/>
                                    </svg>
                                    {{ $t('forms.selectFromDisk') }}
                                </file-upload>
                            </div>

                            <div v-else class="files-queue text-left">
                                <h5 class="mb-3 border-bottom pb-2">{{ $t('forms.prepareToUpload') }} ({{ files.length }})</h5>
                                <b-table responsive hover :items="files" :fields="uploadFields" small class="bg-white border rounded">
                                    <template v-slot:cell(file.name)="data">
                                        <strong>{{ data.item.name }}</strong>
                                        <div class="small text-danger" v-if="data.item.error">{{ data.item.error }}</div>
                                    </template>
                                    <template v-slot:cell(file.size)="data">
                                        {{ humanFileSize(data.item.size) }}
                                    </template>
                                    <template v-slot:cell(file)="data">
                                        <b-badge v-if="data.item.error" variant="danger">{{ $t('forms.statusError') }}</b-badge>
                                        <b-badge v-else-if="data.item.success" variant="success">{{ $t('forms.statusOk') }}</b-badge>
                                        <b-badge v-else-if="data.item.active" variant="warning" class="text-dark">{{ $t('forms.statusSending') }}</b-badge>
                                        <b-badge v-else variant="secondary">{{ $t('forms.statusWaiting') }}</b-badge>
                                    </template>
                                </b-table>

                                <div class="d-flex justify-content-between mt-3">
                                    <b-button variant="outline-danger" size="sm" @click="clearFiles" :disabled="isUploading">{{ $t('forms.cleanList') }}</b-button>

                                    <div>
                                        <file-upload class="btn btn-outline-primary btn-sm mr-2"
                                                     :post-action="uploadUrl" :multiple="true" :headers="authHeader"
                                                     @input-file="inputFile" @input-filter="inputFilter"
                                                     :drop="true" v-model="files" ref="fileUpload">
                                            {{ $t('forms.addNext') }}
                                        </file-upload>
                                        <b-button variant="success" size="sm" v-if="!isUploading" @click.prevent="startUpload">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" class="mr-1" viewBox="0 0 16 16">
                                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                                <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445z"/>
                                            </svg>
                                            {{ $t('forms.startUpload') }}
                                        </b-button>
                                        <b-button variant="danger" size="sm" v-else @click.prevent="stopUpload">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" class="mr-1" viewBox="0 0 16 16">
                                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                                <path d="M5 6.5A1.5 1.5 0 0 1 6.5 5h3A1.5 1.5 0 0 1 11 6.5v3A1.5 1.5 0 0 1 9.5 11h-3A1.5 1.5 0 0 1 5 9.5v-3z"/>
                                            </svg>
                                            {{ $t('forms.stopUpload') }}
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-card>
            </div>
        </div>

        <div class="row">
            <div class="col-lg-12">
                <b-card no-body class="shadow-sm border-0">
                    <div class="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                        <h5 class="m-0 font-weight-bold text-secondary">{{ $t('forms.files') }}</h5>

                        <b-input-group size="sm" style="max-width: 300px;">
                            <b-form-input v-model="searchQuery" type="search" :placeholder="$t('forms.search')"></b-form-input>
                            <b-input-group-append>
                                <b-button disabled variant="outline-secondary"><span aria-hidden="true">🔍</span></b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </div>

                    <div class="card-body p-0">
                        <b-table striped hover :items="items" :fields="fields" :filter="searchQuery"
                                 :current-page="currentPage" :per-page="perPage" class="m-0 border-0"
                                 show-empty :empty-text="$t('forms.fileNotFound')">

                            <template #cell(copyUrl)="row">
                                <b-button size="sm" variant="outline-primary" @click="showUrlAction(row.item)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" class="mr-1" viewBox="0 0 16 16">
                                        <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/>
                                        <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z"/>
                                    </svg>
                                    {{ $t('forms.showUrl') }}
                                </b-button>
                            </template>

                            <template #cell(delete)="row">
                                <b-button size="sm" variant="danger" @click="addFileToDelete(row.item)" v-b-modal.confirm-delete>
                                    &times; {{ $t('forms.delete') }}
                                </b-button>
                            </template>
                        </b-table>
                    </div>

                    <div class="card-footer bg-white d-flex justify-content-center pt-3" v-if="items.length > perPage">
                        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"></b-pagination>
                    </div>
                </b-card>
            </div>
        </div>

        <b-modal id="show-url-modal" ref="modal" :title="formatFilename" header-bg-variant="light" size="lg" ok-only :okTitle="$t('forms.close')">
            <div class="container p-0">
                <b-form-group label="URL:" label-for="url-input" class="mb-0">
                    <b-input-group>
                        <b-form-input :value="formatUrl" type="text" id="url-input" readonly/>
                    </b-input-group>
                </b-form-group>
            </div>
        </b-modal>

        <b-modal id="confirm-delete" :title="$t('forms.warning')" size="md" header-bg-variant="danger" header-text-variant="light" :cancelTitle="$t('forms.cancel')" :okTitle="$t('forms.delete')" @ok="deleteFile">
            <p class="text-center mb-0">{{ $t('forms.deleteConfirmationMessage') }}</p>
        </b-modal>

    </section>
</template>

<script>
import axios from "axios";
import {Const} from "../../main";
import jwtService from "../../mixins/JwtStorageService";
import moment from "moment/moment";

export default {
    name: "FileList",
    data() {
        return {
            items: [],
            editedItem: {},
            files: [],
            isUploading: false,
            uploadUrl: Const.API_HOST + '/admin/file/upload',
            authHeader: {},
            uploadFields: [
                { key: 'file.name', label: this.$t('forms.name') },
                { key: 'file.size', label: this.$t('forms.size') },
                { key: 'file', label: this.$t('forms.status'), thStyle: 'width: 120px;' }
            ],
            searchQuery: '',
            currentPage: 1,
            perPage: 15,
            fields: [
                { key: 'id', label: 'ID', sortable: true, thStyle: 'width: 80px;' },
                {
                    key: 'filename',
                    label: this.$t('forms.name'),
                    sortable: true,
                    formatter: value => { return value ? this.trimEllip(value.substr(7), 80) : ''; }
                },
                {
                    key: 'uploadDate',
                    label: this.$t('forms.created'),
                    sortable: true,
                    formatter: value => { return value ? moment(value).format('DD.MM.YYYY, HH:mm') : ''; }
                },
                { key: 'copyUrl', label: '', thStyle: 'width: 135px;' },
                { key: 'delete', label: '', thStyle: 'width: 100px;' }
            ]
        }
    },
    computed: {
        formatUrl() {
            if (this.editedItem && this.editedItem.filename)
                return Const.WEB_HOST + this.editedItem.filename;
            return '';
        },
        formatFilename() {
            if (this.editedItem && this.editedItem.filename)
                return this.trimEllip(this.editedItem.filename.substr(7), 120);
            return '';
        },
        totalRows() {
            if (!this.searchQuery) return this.items.length;
            let q = this.searchQuery.toLowerCase();
            return this.items.filter(item => item.filename && item.filename.toLowerCase().includes(q)).length;
        }
    },
    mounted() {
        this.fetchFiles();

        const token = jwtService.getAccessToken();
        if (token) {
            this.authHeader = {'Authorization': 'Bearer ' + token}
        }
    },
    methods: {
        fetchFiles() {
            axios.get(Const.API_HOST + '/admin/files')
                .then(response => { this.items = response.data; })
                // eslint-disable-next-line no-console
                .catch(error => console.log(error));
        },
        trimEllip(s, length) {
            if (!s) return "";
            return s.length > length ? s.substring(0, length) + "..." : s;
        },
        showUrlAction(item) {
            this.editedItem = item;
            this.$bvModal.show('show-url-modal');
        },
        addFileToDelete(item) {
            this.editedItem = item;
        },
        deleteFile() {
            axios.post(Const.API_HOST + '/admin/file/remove', {
                id: this.editedItem.id
            }).then(() => {
                this.fetchFiles();
                // eslint-disable-next-line no-console
            }).catch(err => console.log(err));
            this.editedItem = {};
        },

        startUpload() {
            if (this.$refs.fileUpload) {
                this.$refs.fileUpload.active = true;
                this.isUploading = true;
            }
        },
        stopUpload() {
            if (this.$refs.fileUpload) {
                this.$refs.fileUpload.active = false;
                this.isUploading = false;
            }
        },
        clearFiles() {
            this.files = [];
            this.isUploading = false;
            if (this.$refs.fileUpload) {
                this.$refs.fileUpload.clear();
            }
        },
        inputFile: function (newFile, oldFile) {
            if (newFile && oldFile && newFile.success && !oldFile.success) {
                this.fetchFiles();
            }
            if (this.$refs.fileUpload && !this.$refs.fileUpload.active) {
                this.isUploading = false;
            }
        },
        inputFilter: function (newFile, oldFile) {
            if (newFile && !oldFile) {
                // filter files if needed
            }
            newFile.blob = '';
            let URL = window.URL || window.webkitURL;
            if (URL && URL.createObjectURL) {
                newFile.blob = URL.createObjectURL(newFile.file);
            }
        },
        humanFileSize(size) {
            if (!size) return '0 B';
            var i = Math.floor(Math.log(size) / Math.log(1024));
            return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
        }
    }
}
</script>

<style scoped>
.upload-drop-zone {
    border: 2px dashed #ced4da;
    background-color: #f8f9fa;
    transition: all 0.3s ease-in-out;
}

.upload-drop-zone.is-dragover {
    border-color: #007bff;
    background-color: #e9ecef;
    box-shadow: 0 0 10px rgba(0, 123, 255, 0.2);
}

::v-deep .file-uploads label {
    margin-bottom: 0;
    cursor: pointer;
}
</style>